import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_GAP = 8;
const VIEWPORT_MARGIN = 12;
const MAX_MENU_HEIGHT = 320;

export function getBuiltAdminDropdownPosition(rect, viewportHeight) {
  const spaceBelow = Math.max(
    0,
    viewportHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN,
  );
  const spaceAbove = Math.max(0, rect.top - MENU_GAP - VIEWPORT_MARGIN);
  const opensAbove = spaceBelow < MAX_MENU_HEIGHT && spaceAbove > spaceBelow;
  const availableHeight = opensAbove ? spaceAbove : spaceBelow;

  return {
    top: opensAbove ? 'auto' : rect.bottom + MENU_GAP,
    bottom: opensAbove ? viewportHeight - rect.top + MENU_GAP : 'auto',
    maxHeight: Math.min(MAX_MENU_HEIGHT, availableHeight),
  };
}

export default function BuiltAdminDropdown({
  ariaLabel,
  value,
  options,
  placeholder,
  disabled = false,
  disabledLabel = '',
  searchable = false,
  searchPlaceholder = 'Search…',
  persistentOptionValues = [],
  onChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const comboboxRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedLabel = options.find((option) => option.value === value)?.label || '';
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const filteredOptions = searchable && normalizedSearchTerm
    ? options.filter((option) => (
      persistentOptionValues.includes(option.value)
      || option.label.toLocaleLowerCase().includes(normalizedSearchTerm)
    ))
    : options;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        !comboboxRef.current?.contains(event.target)
        && !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return undefined;
    }

    if (!searchable) return undefined;
    const focusFrame = window.requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [isOpen, searchable]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      const rect = comboboxRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const verticalPosition = getBuiltAdminDropdownPosition(rect, viewportHeight);

      setMenuPosition({
        ...verticalPosition,
        left: rect.left,
        width: rect.width,
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    if (!disabled) setIsOpen((current) => !current);
  };

  const selectOption = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="stock-admin-combobox" ref={comboboxRef}>
      <button
        type="button"
        className={`stock-admin-combobox-input stock-admin-select-button${value ? '' : ' stock-admin-select-placeholder'}`}
        onClick={toggleMenu}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {disabled && disabledLabel ? disabledLabel : selectedLabel || placeholder}
      </button>
      <button
        type="button"
        className="stock-admin-combobox-toggle"
        aria-label={`Show ${ariaLabel.toLowerCase()} options`}
        aria-expanded={isOpen}
        onClick={toggleMenu}
        disabled={disabled}
      >
        <span>▾</span>
      </button>
      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="stock-admin-combobox-menu built-admin-combobox-menu"
          style={{
            position: 'fixed',
            top: menuPosition?.top ?? 0,
            bottom: menuPosition?.bottom ?? 'auto',
            left: menuPosition?.left ?? 0,
            right: 'auto',
            width: menuPosition?.width ?? 0,
            maxHeight: menuPosition?.maxHeight ?? 0,
            visibility: menuPosition ? 'visible' : 'hidden',
          }}
        >
          {searchable && (
            <div className="stock-admin-combobox-search-wrap">
              <input
                ref={searchInputRef}
                type="search"
                className="stock-admin-combobox-search"
                aria-label={`Search ${ariaLabel}`}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          )}
          <div className="stock-admin-combobox-options" role="listbox" aria-label={`${ariaLabel} options`}>
            {filteredOptions.map((option) => (
              <button
                key={option.value || '__blank'}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className="stock-admin-combobox-option"
                onClick={() => selectOption(option.value)}
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="stock-admin-combobox-empty">No matching options</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
